import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import MaximizeIcon from '@mui/icons-material/OpenInFull';
import { useSnackbar } from 'material-ui-snackbar-provider';
import Draggable from 'react-draggable';

import { getAppendedMergedDownloadStatus, getUserOrganization, startAppendedMergedDownload } from '../../../../api';
import { useAppDispatch } from '../../../../store/hooks';
import { saveMyOrganization } from '../../../../store/user/userSlice';
import { Portal } from '../../../../helpers/bodyPortal';
import { ReactComponent as MinimizeIcon } from '../../../../assets/icons/minimize.svg';
import Modal from '../../Modal';
import { getDownloadErrorMessage, triggerDownload } from './helpers';
import styles from './DownloadDataModal.module.scss';

type Download = {
  feedId: string;
  title: string;
  progress: number;
  loading: boolean;
  minimized: boolean;
};

const MergedDownloadContext = createContext<((feedId: string, title: string) => Promise<void>) | null>(null);

export const useMergedDownload = () => {
  const startDownload = useContext(MergedDownloadContext);
  if (!startDownload) throw new Error('MergedDownloadProvider is missing');
  return startDownload;
};

export const MergedDownloadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [downloads, setDownloads] = useState<Download[]>([]);
  const activeDownloads = useRef(new Set<string>());
  const notification = useSnackbar();
  const dispatch = useAppDispatch();

  useEffect(() => () => { activeDownloads.current.clear(); }, []);

  const updateDownload = useCallback((feedId: string, changes: Partial<Download>) => {
    setDownloads((previous) => previous.map((download) => download.feedId === feedId ? { ...download, ...changes } : download));
  }, []);

  const startDownload = useCallback(async (feedId: string, title: string) => {
    if (activeDownloads.current.has(feedId)) {
      updateDownload(feedId, { minimized: false });
      return;
    }

    activeDownloads.current.add(feedId);
    setDownloads((previous) => [...previous, { feedId, title, progress: 0, loading: true, minimized: false }]);

    try {
      let result = (await startAppendedMergedDownload(feedId)).data;
      if (!activeDownloads.current.has(feedId)) return;
      updateDownload(feedId, { loading: false, progress: Math.max(0, Math.min(100, Number(result.progress) || 0)) });

      if (result.status === 'PROCESSING') {
        notification.showMessage('Your merged MP4 is being prepared.');
      }

      while (result.status === 'PROCESSING') {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        if (!activeDownloads.current.has(feedId)) return;
        result = (await getAppendedMergedDownloadStatus(result.jobId)).data;
        if (!activeDownloads.current.has(feedId)) return;
        updateDownload(feedId, { progress: Math.max(0, Math.min(100, Number(result.progress) || 0)) });
      }

      if (result.status !== 'READY' || !result.url) {
        throw new Error(result.errorMessage || 'Failed to create merged MP4');
      }

      updateDownload(feedId, { progress: 100 });
      triggerDownload(result.url);
      try {
        const { data } = await getUserOrganization(true);
        if (activeDownloads.current.has(feedId)) dispatch(saveMyOrganization(data));
      } catch (error) {
        console.error('Failed to refresh download hours usage', error);
      }
    } catch (error) {
      if (activeDownloads.current.has(feedId)) {
        notification.showMessage(getDownloadErrorMessage(error) || 'Failed to create merged MP4');
      }
    } finally {
      if (activeDownloads.current.delete(feedId)) {
        setDownloads((previous) => previous.filter((download) => download.feedId !== feedId));
      }
    }
  }, [dispatch, notification, updateDownload]);

  return (
    <MergedDownloadContext.Provider value={startDownload}>
      {children}
      {downloads.map((download) => (
        <MergedDownloadProgress
          key={download.feedId}
          download={download}
          setMinimized={(minimized) => updateDownload(download.feedId, { minimized })}
        />
      ))}
    </MergedDownloadContext.Provider>
  );
};

const MergedDownloadProgress = ({ download, setMinimized }: { download: Download; setMinimized: (minimized: boolean) => void }) => {
  const wasDragged = useRef(false);

  return (
    <>
      <Modal open={!download.minimized}>
        <div className={styles.modalBody}>
          <div className={styles.modalTitle}>
            <div className={styles.modalTop}>
              <div className={styles.titleText}>Preparing merged MP4</div>
              <button type="button" className={styles.minimizeButton} onClick={() => setMinimized(true)} aria-label="Minimize download">
                <MinimizeIcon />
              </button>
            </div>
          </div>
          <div className={styles.progressCard}>
            <LinearProgress variant={download.loading ? 'indeterminate' : 'determinate'} value={download.progress} />
            <div className={styles.progressText}>Generating merged MP4... {Math.round(download.progress)}%</div>
          </div>
        </div>
      </Modal>
      {download.minimized && (
        <Portal>
          <div
            className={styles.minimizedLayer}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onMouseOver={(event) => event.stopPropagation()}
            onContextMenu={(event) => event.stopPropagation()}
            onDragStart={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <Draggable
              bounds="parent"
              onStart={() => { wasDragged.current = false; }}
              onDrag={() => { wasDragged.current = true; }}
            >
              <button
                type="button"
                className={styles.minimizedModal}
                onClick={(event) => {
                  event.stopPropagation();
                  if (!wasDragged.current) setMinimized(false);
                }}
              >
                <span className={styles.minimizedText}>
                  <span className={styles.minimizedTitle}>{download.title}</span>
                  <span className={styles.minimizedProgress}>Generating merged MP4... {Math.round(download.progress)}%</span>
                </span>
                <MaximizeIcon className={styles.maximizeIcon} />
              </button>
            </Draggable>
          </div>
        </Portal>
      )}
    </>
  );
};
